import { Stedfesting } from '@/domain/vegobjekter/Stedfesting';
import { SuperStedfesting } from '@/domain/vegnett/SuperStedfesting';
import { Innhold, VegobjekttypeStedfesting } from '@/domain/vegobjekter/Vegobjekttype';
import type {
    EgenskapstypeStedfesting,
    Stedfesting as ApiDatakatalogStedfesting,
} from '@/generated/nvdb/datakatalog/models';
import type {
    Retning,
    Stedfesting as ApiVegobjektStedfestingResponse,
} from '@/generated/nvdb/vegobjekter/models';

type ApiVegobjekttypeStedfesting = ApiDatakatalogStedfesting & {
    datatype?: string;
    geometritype?: string;
    innhold?: EgenskapstypeStedfesting | null;
};

type ApiSuperstedfesting = Omit<
    ApiVegobjektStedfestingResponse,
    'sideposisjon' | 'type'
> & {
    sideposisjon?: number;
};

function requireValue<T>(value: T | undefined, fieldName: string): NonNullable<T> {
    if (value === undefined || value === null) {
        throw new Error(`Stedfesting mangler ${fieldName}`);
    }

    return value as NonNullable<T>;
}

export function transformVegobjekttypeStedfesting(
    data: ApiVegobjekttypeStedfesting | null | undefined
): VegobjekttypeStedfesting | null {
    return data
        ? new VegobjekttypeStedfesting(
              data.id,
              data.navn ?? '',
              data.kortnavn ?? '',
              data.kategori,
              data.egenskapstype,
              data.datatype ?? '',
              data.sensitivitet,
              data.sorteringsnummer,
              data.viktighet,
              data.skrivebeskyttet,
              data.nøyaktighetskrav_grunnriss,
              data.høydereferanse_tall,
              transformStedfestingInnhold(data.innhold)!,
              data.geometritype ?? ''
          )
        : null;

    function transformStedfestingInnhold(
        innholdData: EgenskapstypeStedfesting | null | undefined
    ) {
        return innholdData
            ? new Innhold(
                  innholdData.id,
                  innholdData.navn ?? '',
                  innholdData.kortnavn ?? '',
                  innholdData.kategori,
                  innholdData.geometritype ?? ''
              )
            : null;
    }
}

export function transformVegobjektStedfesting(
    data: ApiVegobjektStedfestingResponse | null | undefined
) {
    if (!data) return [];
    if (data.type === 'Sving') {
        const startpunkt = requireValue(data.startpunkt, 'startpunkt');
        const sluttpunkt = requireValue(data.sluttpunkt, 'sluttpunkt');

        return [
            new Stedfesting(
                requireValue(
                    startpunkt.veglenkesekvensid,
                    'startpunkt.veglenkesekvensid'
                ),
                requireValue(startpunkt.kortform, 'startpunkt.kortform'),
                requireValue(startpunkt.relativPosisjon, 'startpunkt.relativPosisjon'),
                requireValue(startpunkt.relativPosisjon, 'startpunkt.relativPosisjon'),
                startpunkt.retning as Retning,
                startpunkt.sideposisjon,
                startpunkt.kjørefelt || [],
                startpunkt.type || 'Punkt'
            ),
            new Stedfesting(
                requireValue(
                    sluttpunkt.veglenkesekvensid,
                    'sluttpunkt.veglenkesekvensid'
                ),
                requireValue(sluttpunkt.kortform, 'sluttpunkt.kortform'),
                requireValue(sluttpunkt.relativPosisjon, 'sluttpunkt.relativPosisjon'),
                requireValue(sluttpunkt.relativPosisjon, 'sluttpunkt.relativPosisjon'),
                sluttpunkt.retning as Retning,
                sluttpunkt.sideposisjon,
                sluttpunkt.kjørefelt || [],
                sluttpunkt.type || 'Punkt'
            ),
        ];
    }

    return [
        new Stedfesting(
            requireValue(data.veglenkesekvensid, 'veglenkesekvensid'),
            requireValue(data.kortform, 'kortform'),
            data.startposisjon === undefined
                ? requireValue(data.relativPosisjon, 'relativPosisjon')
                : data.startposisjon,
            data.sluttposisjon === undefined
                ? requireValue(data.relativPosisjon, 'relativPosisjon')
                : data.sluttposisjon,
            data.retning as Retning,
            data.sideposisjon,
            data.kjørefelt,
            data.type
        ),
    ];
}

export function transformSuperstedfesting(
    data: ApiSuperstedfesting | null | undefined
): SuperStedfesting | null {
    if (!data) return null;
    return new SuperStedfesting(
        requireValue(data.veglenkesekvensid, 'veglenkesekvensid'),
        data.startposisjon === undefined
            ? requireValue(data.relativPosisjon, 'relativPosisjon')
            : data.startposisjon,
        data.sluttposisjon === undefined
            ? requireValue(data.relativPosisjon, 'relativPosisjon')
            : data.sluttposisjon,
        data.relativPosisjon as number,
        data.retning as Retning,
        data.sideposisjon as number,
        data.kjørefelt ?? [],
        data.kortform as string
    );
}
